import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Home, RotateCcw } from 'lucide-react';
import { isDynamicImportError, recoverFromResourceLoadError } from '@/utils/versionCheck';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * 错误边界组件
 * 用于捕获子组件树中的 JavaScript 错误，记录错误并显示友好的错误页面
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 记录错误信息
    console.error('ErrorBoundary 捕获到错误:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    if (!import.meta.env.DEV && isDynamicImportError(error)) {
      void recoverFromResourceLoadError(error);
    }

    // 在生产环境中，可以将错误信息发送到错误监控服务
    if (import.meta.env.MODE === 'production') {
      // 这里可以集成 Sentry 等错误监控服务
      // Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }
  }

  handleReload = () => {
    // 重新加载页面
    window.location.reload();
  };

  handleGoHome = () => {
    // 跳转到首页
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // 如果提供了自定义的 fallback UI，则使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认的错误页面
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 bg-background text-foreground">
          <div className="text-center space-y-6 max-w-md">
            {/* 错误标题和描述 */}
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">页面出现错误</h1>
              <p className="text-muted-foreground">
                抱歉，页面出现了意外错误。您可以尝试刷新页面或返回首页。
              </p>
            </div>

            {/* 操作按钮 */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReload} className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                刷新页面
              </Button>
              <Button
                variant="outline"
                onClick={this.handleGoHome}
                className="flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                返回首页
              </Button>
            </div>
          </div>

          {/* 开发环境下显示详细错误信息 */}
          {import.meta.env.MODE === 'development' && this.state.error && (
            <details className="mt-8 w-full max-w-2xl">
              <summary className="cursor-pointer mb-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                点击查看详细错误信息
              </summary>
              <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs font-mono text-muted-foreground border">
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
